import React, { ForwardedRef, useCallback, useMemo } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { format, secondsToMilliseconds, toDate } from 'date-fns';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { setPreviouslyWatchedChannel } from 'store/epg/actions';
import { showPlayer } from 'store/player/actions';
import { StreamTypes } from 'store/player/actions.types';
import { selectChannelsById } from 'store/programs/selectors';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import { selectActiveUserProfile } from 'store/user/selectors';

import parseVideoItem from 'services/data-manager/parsing/parseVideoItem';
import { ContentType, NowNextData } from 'services/data-manager/types';

import { useCast } from 'utils/cast/useCast';
import fetchLiveProgram from 'utils/fetchLiveProgram';
import isFunction from 'utils/function';
import getOrdinal from 'utils/getOrdinary';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import { createViewableNowNextId } from 'utils/hooks/useViewableItems';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';

import { DetailsRequestUrlType } from 'screens/Detail/types';
import { isLive } from 'screens/Epg/utils/program';

import BaseTileImage from 'components/BaseTileImage';
import ContentMarkerIcons from 'components/ContentMarkerIcons';
import RibbonTouchable from 'components/CustomButtons/RibbonTouchable';
import Marker from 'components/Marker';
import {
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';
import { LockedContentCard } from 'components/Ribbon/components/LockedContentCard';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

import ProgramLogo from '../../components/ProgramLogo';
import ProgressBar from '../../components/ProgressBar';
import { TileTitle } from '../../components/TileTitle';
import { PLACEHOLDER_ICON_CENTER } from '../../constants';
import useParentalRating from '../../hooks/useParentalRating';
import gRibbonItemStyles from '../styles';

const {
    CARDS_TITLE_CONTENT_LOCKED,
    ACCESSIBILITY: {
        ACCESSIBILITY_COLLECTION_FIRST_ASSET,
        STRIPE: { ACCESSIBILITY_STRIPE_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;

type Props = {
    item: NowNextData;
    visibleItems: Record<string, boolean>;
    isCollection?: boolean;
    testID?: string;
    index?: number;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
    totalNumberOfItems?: number;
} & OnUavAnalyticsProps &
    StandardUavProps;

const ProgramNowNextRibbon = (
    props: Props,
    forwardedRef?: ForwardedRef<View | undefined>,
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { isCastConnected, cast } = useCast();
    const navigation =
        useNavigation<
            StackNavigationProp<RouteNavigationParams, SCREEN.DETAILS>
        >();

    const {
        item,
        visibleItems,
        isCollection = false,
        onRailsSelection,
        railId,
        templateId,
        testID,
        onShowPinDialog,
        onSetParentalControlCheckedCallback,
        index = 0,
        totalNumberOfItems,
    } = props;

    const channelsById = useSelector(selectChannelsById);
    const { primary } = useSelector(selectLayoutConfig);
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const profileParentalControlRating = useProfileParentalRating();

    const {
        channelId,
        programmes: [currentItem, nextItem],
    } = item;
    const isSeries = !!currentItem?.seriesRef;

    const parentalRating = useParentalRating(currentItem);

    const isContentLocked = needsToLockContent(
        profileParentalControlRating,
        parentalRating,
    );

    const nowTime =
        currentItem?.period.start &&
        format(
            toDate(secondsToMilliseconds(currentItem.period.start)),
            'HH:mm',
        );
    const nextTime =
        nextItem?.period.start &&
        format(toDate(secondsToMilliseconds(nextItem.period.start)), 'HH:mm');

    const handleOpenDetailsPage = useCallback(() => {
        let params: RouteNavigationParams[SCREEN.DETAILS] = {
            templateId,
            railId,
            filterType: DetailsRequestUrlType.PROGRAM,
            id: currentItem?.editorial.id,
            channelId,
        };

        if (currentItem?.seriesRef) {
            params = {
                ...params,
                seriesId: currentItem.seriesRef,
                season: currentItem.SeasonNumber,
                episode: currentItem.episodeNumber,
            };
        }

        dispatch(
            startSentryTransaction(SENTRY_TRANSACTIONS.DetailPageRendering),
        );
        navigation.navigate(SCREEN.DETAILS, params);
    }, [navigation, currentItem, channelId, templateId, railId]);

    const handlePress = useCallback(async () => {
        const channel = channelsById[channelId];
        let liveProgram = currentItem;

        if (!isLive(currentItem)) {
            try {
                const maybeLiveProgram = await fetchLiveProgram(
                    channel.editorial.id,
                );

                if (maybeLiveProgram) {
                    liveProgram = maybeLiveProgram;
                }
            } catch (e) {
                // use currentItem
            }
        }

        const videoItem = parseVideoItem({
            channel,
            program: liveProgram,
        });

        const playbackType = StreamTypes.live;

        if (isFunction(onRailsSelection) && liveProgram) {
            onRailsSelection({ id: liveProgram.id, playbackType, index });
        }

        if (isContentLocked && isFunction(onShowPinDialog)) {
            if (isFunction(onSetParentalControlCheckedCallback)) {
                onSetParentalControlCheckedCallback(handleOpenDetailsPage);
            }

            onShowPinDialog();
        } else if (isCastConnected) {
            cast(videoItem, StreamTypes.live);

            if (videoItem.channelId && selectedProfileId) {
                dispatch(
                    setPreviouslyWatchedChannel({
                        channelId: videoItem.channelId,
                        profileId: selectedProfileId,
                    }),
                );
            }
        } else {
            dispatch(startSentryTransaction(SENTRY_TRANSACTIONS.ChannelTune));
            dispatch(
                showPlayer({
                    videoItem,
                    playbackType,
                    railId,
                    templateId,
                }),
            );
        }
    }, [
        channelId,
        cast,
        channelsById,
        railId,
        templateId,
        isContentLocked,
        isCastConnected,
        currentItem,
        selectedProfileId,
        handleOpenDetailsPage,
        onRailsSelection,
        onShowPinDialog,
        onSetParentalControlCheckedCallback,
        index,
    ]);

    const accessibilityLabel = useMemo(() => {
        const firstAssetKey = isCollection
            ? ACCESSIBILITY_COLLECTION_FIRST_ASSET
            : ACCESSIBILITY_STRIPE_FIRST_ASSET;
        const orderAssetKey = 'ACCESSIBILITY_STRIPE_%NUMBER%_ASSET';

        if (isContentLocked && index === 0) {
            return `${t(CARDS_TITLE_CONTENT_LOCKED)} ${t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}`;
        }

        if (isContentLocked) {
            return `${t(CARDS_TITLE_CONTENT_LOCKED)} ${t(orderAssetKey, {
                number: getOrdinal(index + 1),
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}`;
        }

        let label = '';
        if (currentItem) {
            label += `${nowTime} ${currentItem.title || ''} `;
        }

        if (index === 0) {
            label += t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        } else {
            label += t(orderAssetKey, {
                number: getOrdinal(index + 1),
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        }

        return label;
    }, [
        isContentLocked,
        currentItem,
        nowTime,
        index,
        totalNumberOfItems,
        t,
        isCollection,
    ]);

    return (
        <RibbonTouchable
            ref={forwardedRef}
            testID={testID}
            onPress={handlePress}
            metadataType={ContentType.NowNext}
            isCollection={isCollection}
            railId={railId}
            accessibilityLabel={accessibilityLabel}
            RibbonContentComponent={
                <>
                    {isContentLocked ? (
                        <LockedContentCard
                            channelId={currentItem?.serviceRef}
                        />
                    ) : (
                        <>
                            <BaseTileImage
                                contentId={currentItem?.id || ''}
                                contentType={
                                    isSeries
                                        ? ImageContentType.EPISODE
                                        : currentItem?.editorial?.contentType
                                }
                                imageType={
                                    isSeries
                                        ? ImageType.EPISODE
                                        : ImageType.BANNER
                                }
                                width={ImageSize.LANDSCAPE.width}
                                style={gStyles.flex}
                                placeHolder={PLACEHOLDER_ICON_CENTER}
                                resizeMode="cover"
                            />
                            {!!currentItem && (
                                <ProgressBar
                                    start={currentItem.period.start}
                                    end={currentItem.period.end}
                                    contentType={ContentType.NowNext}
                                    isCollection={isCollection}
                                    backgroundColor={primary}
                                    visibleItems={visibleItems}
                                    itemId={createViewableNowNextId(item)}
                                />
                            )}
                            <ProgramLogo channelId={currentItem?.serviceRef} />
                            {!!(
                                currentItem &&
                                currentItem.period.start &&
                                currentItem.period.end
                            ) && (
                                <Marker
                                    startTime={currentItem.period.start}
                                    endTime={currentItem.period.end}
                                    bgColor={primary}
                                    style={[
                                        gRibbonItemStyles.marker,
                                        gRibbonItemStyles.isOnLiveCard,
                                    ]}
                                    textStyle={gRibbonItemStyles.markerText}
                                />
                            )}
                            {currentItem && (
                                <ContentMarkerIcons
                                    parentalRating={parentalRating}
                                    shouldDisplayCatchup={false}
                                    isLimitedToOne
                                    isOnLiveCard
                                    isOnCard
                                />
                            )}
                        </>
                    )}
                </>
            }
            RibbonInfoComponent={
                <TileTitle
                    title={
                        isContentLocked
                            ? t(CARDS_TITLE_CONTENT_LOCKED)
                            : currentItem &&
                              `${nowTime} ${currentItem?.title || ''}`
                    }
                    subTitle={
                        (nextItem &&
                            !isContentLocked &&
                            `${nextTime} ${nextItem?.title || ''}`) ||
                        undefined
                    }
                />
            }
        />
    );
};

export default React.forwardRef(ProgramNowNextRibbon);
